import * as React from "react";
import {useEffect} from "react";
import {Editor, Transforms} from "slate";
import * as SlateReact from "slate-react";

import {useMathEditorAPIContext} from "@khan/guide-math-editor-context";
import type {ChatInputMetadata} from "@khan/guide-types";

export function useHandleSend({
    isSendDisabled,
    onSend,
    messageContainsSpeechToTextContentRef,
    isVoiceOn,
    selectedVoice,
}: {
    isSendDisabled: boolean;
    onSend: (metadata: ChatInputMetadata) => unknown;
    messageContainsSpeechToTextContentRef: React.MutableRefObject<boolean>;
    isVoiceOn: boolean;
    selectedVoice: string | null;
}) {
    const {setActiveInputs: setActiveMathInputs, setSendMessage} =
        useMathEditorAPIContext();
    const editor = SlateReact.useSlate();

    const handleSend = React.useCallback(() => {
        if (isSendDisabled) {
            return;
        }

        Transforms.delete(editor, {
            at: {
                anchor: Editor.start(editor, []),
                focus: Editor.end(editor, []),
            },
        });

        setActiveMathInputs({});

        onSend({
            containsSpeechToTextContent:
                messageContainsSpeechToTextContentRef.current,
            guideVoiceDescriptor: isVoiceOn ? selectedVoice : null,
            prepopulatedText: null,
            requestUIType: "USER_TYPED",
            requestWithTextToSpeech: isVoiceOn,
            // TODO(GL-1309): Move the timeToSubmit computation into this
            // component from chat.tsx
            timeToSubmit: 0,
        });

        // Once we send the chat message, reset the flags used in the metadata
        // for the next message.
        messageContainsSpeechToTextContentRef.current = false;
    }, [
        editor,
        isSendDisabled,
        isVoiceOn,
        selectedVoice,
        setActiveMathInputs,
        messageContainsSpeechToTextContentRef,
        onSend,
    ]);

    useEffect(() => {
        setSendMessage(() => handleSend);
    }, [setSendMessage, handleSend]);

    return handleSend;
}
